import { useEffect } from 'react';
import { MantineProvider, Stack, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { fetchIngredientCatalog } from './services/ingredientService';
import { setIngredientCatalog } from './data/ingredientDatabase';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { PostHogProvider, usePostHog } from '@posthog/react';
import { useAuth } from './hooks/useAuth';
import { useOnboarding, OnboardingProvider } from './contexts/OnboardingContext';
import { CookingTimersProvider } from './contexts/CookingTimersContext';
import { CookingTimersTray } from './components/Recipe/CookingTimersTray';
import { useDelayedLoading } from './hooks/useDelayedLoading';
import { usePostHogPageview } from './hooks/usePostHogPageview';
import { AppHeader } from './components/Layout/AppHeader';
import { AppFooter } from './components/Layout/AppFooter';
import { ImpersonationBanner } from './components/Layout/ImpersonationBanner';
import { PageLayout } from './components/Layout/PageLayout';
import { ResponsiveContainer } from './components/Layout/ResponsiveContainer';
import { LoadingScreen } from './components/Layout/LoadingScreen';
import { LandingPage } from './components/Marketing/LandingPage';
import { FloatingContactButton } from './components/Contact';
import { OnboardingWizard } from './components/Onboarding/OnboardingWizard';
import { MainDashboard } from './components/Dashboard/MainDashboard';
import { MealPlanningPage } from './pages/MealPlanningPage';
import { MealPlanPage } from './pages/MealPlanPage';
import { MealPlanHistoryPage } from './pages/MealPlanHistoryPage';
import { CookbookPage } from './pages/CookbookPage';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { RecipeArchivePage } from './pages/RecipeArchivePage';
import { RecipeDetailsPage } from './pages/RecipeDetailsPage';
import { CookViewPage } from './pages/CookViewPage';
import { PantryPage } from './pages/PantryPage';
import { QuickBitesPage } from './pages/QuickBitesPage';
import { ProfilePage } from './pages/ProfilePage';
import { BillingPage } from './pages/BillingPage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { SubscriptionCanceledPage } from './pages/SubscriptionCanceledPage';
import { AboutPage } from './pages/AboutPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { CampaignLandingPage } from './components/Marketing/CampaignLandingPage';
import { LifestyleLandingPage } from './components/Marketing/LifestyleLandingPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminSystemPage } from './pages/admin/AdminSystemPage';
import { AdminImportsPage } from './pages/admin/AdminImportsPage';
import { AdminReleasesPage } from './pages/admin/AdminReleasesPage';
import { AdminVouchersPage } from './pages/admin/AdminVouchersPage';
import { AdminEmailsPage } from './pages/admin/AdminEmailsPage';
import { CatalogRecipeDetailsPage } from './pages/CatalogRecipeDetailsPage';
import { SeedQueuePage } from './pages/SeedQueuePage';
import { CatalogBrowserPage } from './pages/CatalogBrowserPage';
import { TrendingBrowserPage } from './pages/TrendingBrowserPage';
import { TasteAxesPage } from './pages/TasteAxesPage';
import { TasteAxisDetailPage } from './pages/TasteAxisDetailPage';
import { IngredientBrowserPage } from './pages/IngredientBrowserPage';
import { IngredientDetailPage } from './pages/IngredientDetailPage';
import { AuthActionPage } from './pages/AuthActionPage';
import { AuthPage } from './pages/AuthPage';
import { CookieConsentComponent } from './components/CookieConsent/CookieConsent';
import { AppUpdateNotifier } from './components/UI/AppUpdateNotifier';
import { useScrollToTop } from './hooks/useScrollToTop';
import { captureUtmParams, getUtmParams } from './utils/utm';
import { theme, notificationTheme } from './theme';

const queryClient = new QueryClient();

// Check if PostHog is enabled via environment variable
const isPostHogEnabled = process.env.REACT_APP_ENABLE_POSTHOG === 'true';

function ScrollToTopWrapper({ children }: { children: React.ReactNode }) {
  useScrollToTop();
  usePostHogPageview();

  // Capture UTM parameters on first landing page load
  useEffect(() => {
    captureUtmParams();
  }, []);

  return <>{children}</>;
}

// User identification wrapper
function PostHogUserIdentifier({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      if (user) {
        const utm = getUtmParams();
        posthog.identify(user.uid, {
          email: user.email,
          name: user.displayName,
          ...utm,
        });
      } else {
        posthog.reset();
      }
    }
  }, [user, posthog]);

  return <>{children}</>;
}

// Loads the ingredient catalog from the backend once the user is authenticated
// and caches it in-process (see data/ingredientDatabase). The catalog replaces
// the old bundled static list that the search/normalization helpers read from.
// Renders nothing; refetches if the signed-in user changes.
function IngredientCatalogLoader() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    user
      .getIdToken()
      .then(fetchIngredientCatalog)
      .then((ingredients) => {
        if (!cancelled) setIngredientCatalog(ingredients);
      })
      .catch((err) =>
        console.error('Failed to load ingredient catalog:', err)
      );
    return () => {
      cancelled = true;
    };
  }, [user]);

  return null;
}

// Conditional PostHog wrapper - only provides PostHog in production
function ConditionalPostHogProvider({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled) {
    console.log('[PostHog] Disabled in non-production environment');
    return <>{children}</>;
  }

  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_API_KEY || ''}
      options={{
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        // Auth action links carry single-use codes in the query string, and a
        // sign-in link's URL is enough to take over an account. AuthActionPage
        // strips them from the address bar on mount; this covers the window
        // before that runs and anything else that ever puts one in a URL.
        sanitize_properties: (properties) => {
          const scrub = (value: unknown) => {
            if (typeof value !== 'string' || !value.includes('oobCode')) return value;
            try {
              const url = new URL(value);
              ['oobCode', 'e', 'apiKey', 'continueUrl'].forEach((p) =>
                url.searchParams.delete(p)
              );
              return url.toString();
            } catch {
              return value;
            }
          };
          return {
            ...properties,
            $current_url: scrub(properties.$current_url),
            $referrer: scrub(properties.$referrer),
          };
        },
        capture_pageview: false, // We'll handle this manually with React Router
        capture_pageleave: true,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: { password: true },
        },
        autocapture: true,
      }}
    >
      {children}
    </PostHogProvider>
  );
}

// Redirect component for legacy recipe routes

// Redirect authenticated users from marketing pages to dashboard
function MarketingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return (
    <Stack gap={0} style={{ minHeight: '100vh' }}>
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Box
          component="header"
          h={{ base: 56, sm: 60 }}
          style={{
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <AppHeader />
        </Box>
      </Box>

      <Box
        component="main"
        style={{ flex: 1 }}
      >
        {children}
      </Box>

      <AppFooter />
      <FloatingContactButton />
    </Stack>
  );
}

function usePinterestRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pinterest') === 'connected') {
      const returnPath = localStorage.getItem('pinterest_return_path');
      localStorage.removeItem('pinterest_return_path');
      if (returnPath && returnPath !== '/' && returnPath !== window.location.pathname) {
        navigate(`${returnPath}?pinterest=connected`, { replace: true });
      }
    }
  }, [navigate]);
}

function useGoogleCalendarRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_calendar') === 'connected') {
      // Redirect to profile page with the google_calendar param so the component can handle it
      if (window.location.pathname !== '/profile') {
        navigate('/profile?google_calendar=connected', { replace: true });
      }
    }
    const error = params.get('error');
    if (error === 'google_calendar_auth_failed' || error === 'google_calendar_no_refresh_token') {
      if (window.location.pathname !== '/profile') {
        navigate(`/profile?error=${error}`, { replace: true });
      }
    }
  }, [navigate]);
}

// Public, server-rendered routes (recipe / profile / plan deep links). These
// pages are seeded with server-rendered content (#__RECIPE__) so they can
// render on the first paint. Kept in its own component so it can be rendered
// both during the initial auth/onboarding loading window and after auth
// resolves to an anonymous user — without remounting in between.
function PublicRouteLayout() {
  return (
    <Stack gap={0} style={{ minHeight: '100vh' }}>
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <ImpersonationBanner />
        <Box
          component="header"
          h={{ base: 56, sm: 60 }}
          style={{
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <AppHeader />
        </Box>
      </Box>

      <Box
        component="main"
        px={{ base: 'xs', sm: 'md' }}
        pb={{ base: '8rem', sm: '10rem' }}
        style={{ flex: 1 }}
      >
        <ResponsiveContainer>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/u/:userId" element={<PublicProfilePage />} />
            {/* Friendly URL routes */}
            <Route path="/recipe/:slugWithId" element={<RecipeDetailsPage />} />
            <Route path="/recipe/:slugWithId/cook" element={<CookViewPage />} />
            <Route path="/plan/:slugWithId" element={<MealPlanPage />} />
            {/* Public meal plan route */}
            <Route path="/u/:userId/plan/:planId" element={<MealPlanPage />} />
            {/* New singular recipe path */}
            <Route path="/u/:userId/recipe/:recipeId" element={<RecipeDetailsPage />} />
            <Route path="/u/:userId/recipe/:recipeId/cook" element={<CookViewPage />} />
            {/* Legacy plural recipes path for backward compatibility */}
            <Route path="/u/:userId/recipes/:recipeId" element={<RecipeDetailsPage />} />
            <Route path="/u/:userId/recipes/:recipeId/cook" element={<CookViewPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ResponsiveContainer>
      </Box>

      <AppFooter />
      <FloatingContactButton />
    </Stack>
  );
}

function AppContent() {
  usePinterestRedirect();
  useGoogleCalendarRedirect();
  const { user, loading: authLoading } = useAuth();
  const { hasCompletedOnboarding, loading: onboardingLoading, completeOnboarding } = useOnboarding();
  const launchRestoreNavigate = useNavigate();

  // Launch-email CTAs mostly reach logged-out users (the campaign audience
  // is dormant accounts), and the logged-out tree's wildcard redirect eats
  // /planning?launch=... before they can sign in. The param is stashed below
  // at redirect time; once a user is signed in, restore the claim flow.
  useEffect(() => {
    if (!user) return;
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem('pendingLaunch');
    } catch {
      return;
    }
    if (!pending) return;
    try {
      sessionStorage.removeItem('pendingLaunch');
    } catch {}
    launchRestoreNavigate(`/planning?launch=${encodeURIComponent(pending)}`, { replace: true });
  }, [user, launchRestoreNavigate]);

  const showAuthLoading = useDelayedLoading(authLoading, 300);
  const showOnboardingLoading = useDelayedLoading(onboardingLoading, 500);

  const path = window.location.pathname;
  const isPublicRoute =
    path.startsWith('/u/') || path.startsWith('/recipe/') || path.startsWith('/plan/');

  // Public deep links are server-rendered and seeded, so render them
  // immediately during the initial auth/onboarding loading window instead of a
  // blank/loading screen — otherwise the server-rendered content flashes out
  // and back in once the bundle boots. This only triggers on the initial load
  // (auth/onboarding still resolving); in-app navigation happens after they've
  // resolved and falls through to the normal layouts below.
  if (isPublicRoute && (authLoading || (user && onboardingLoading))) {
    return <PublicRouteLayout />;
  }

  // Show loading for auth or while we're determining what to show
  if (authLoading && showAuthLoading) {
    return <LoadingScreen />;
  }

  // Show loading for onboarding check (only for authenticated users)
  if (user && onboardingLoading && showOnboardingLoading) {
    return <LoadingScreen />;
  }

  // If we're still loading auth but not showing the spinner yet, don't show anything else
  if (authLoading) {
    return null;
  }

  // If we're still loading onboarding status but not showing spinner yet, don't show anything else
  if (user && onboardingLoading) {
    return null;
  }

  // Show landing page only if we're sure the user is not authenticated
  // (avoid showing it during the initial auth check)
  // BUT: allow access to public routes like /u/:userId/recipe/:recipeId
  if (!user && !authLoading) {
    // Stash the launch param before the logged-out tree's wildcard redirect
    // drops it (synchronous render-time write on purpose: the redirect fires
    // in the first effect flush, before any effect here could run)
    const launchParam = new URLSearchParams(window.location.search).get('launch');
    if (launchParam && /^[a-z0-9-]{1,64}$/.test(launchParam)) {
      try {
        sessionStorage.setItem('pendingLaunch', launchParam);
      } catch {}
      // They clicked an email CTA with intent; the marketing landing page is
      // a detour. Straight to sign-in, and the stash restores the claim flow
      // (trial re-up, planning wizard) the moment auth completes.
      return <Navigate to="/signin" replace />;
    }

    if (isPublicRoute) {
      // Render public routes without requiring authentication
      return <PublicRouteLayout />;
    }

    return (
      <Stack gap={0} style={{ minHeight: '100vh' }}>
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <ImpersonationBanner />
          <Box
            component="header"
            h={{ base: 56, sm: 60 }}
            style={{
              backgroundColor: 'var(--mantine-color-body)',
            }}
          >
            <AppHeader />
          </Box>
        </Box>

        <Box
          component="main"
          style={{ flex: 1 }}
        >
          <LandingPage />
        </Box>

        <AppFooter />
        <FloatingContactButton />
      </Stack>
    );
  }

  // Show onboarding for new users
  if (hasCompletedOnboarding === false) {
    return (
      <Stack gap={0} style={{ minHeight: '100vh' }}>
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <ImpersonationBanner />
          <Box
            component="header"
            h={{ base: 56, sm: 60 }}
            style={{
              backgroundColor: 'var(--mantine-color-body)',
            }}
          >
            <AppHeader />
          </Box>
        </Box>

        <Box
          component="main"
          px={{ base: 'xs', sm: 'md' }}
          pb={{ base: '8rem', sm: '10rem' }}
          style={{ flex: 1 }}
        >
          <OnboardingWizard onComplete={completeOnboarding} />
        </Box>
        
        <AppFooter />
      </Stack>
    );
  }

  // Show main dashboard only for users who have definitely completed onboarding
  if (hasCompletedOnboarding === true) {
    return (
      <Stack gap={0} style={{ minHeight: '100vh' }}>
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <ImpersonationBanner />
          <Box
            component="header"
            h={{ base: 56, sm: 60 }}
            style={{
              backgroundColor: 'var(--mantine-color-body)',
            }}
          >
            <AppHeader />
          </Box>
        </Box>

        <Box
          component="main"
          px={{ base: 'xs', sm: 'md' }}
          pb={{ base: '8rem', sm: '10rem' }}
          style={{ flex: 1 }}
        >
          <ResponsiveContainer>
            <Routes>
              <Route path="/" element={<MainDashboard />} />
              <Route path="/planning" element={<MealPlanningPage />} />
              <Route path="/meal-plans" element={<MealPlanHistoryPage />} />
              <Route path="/quick-bites" element={<QuickBitesPage />} />
              <Route path="/cookbook" element={<CookbookPage />} />
              <Route path="/archive" element={<RecipeArchivePage />} />
              {/* New canonical public routes */}
              <Route path="/u/:userId" element={<PublicProfilePage />} />
              <Route path="/u/:userId/plan/:planId" element={<MealPlanPage />} />
              <Route path="/u/:userId/recipe/:recipeId" element={<RecipeDetailsPage />} />
              <Route path="/u/:userId/recipe/:recipeId/cook" element={<CookViewPage />} />
              {/* Legacy plural recipes path for backward compatibility */}
              <Route path="/u/:userId/recipes/:recipeId" element={<RecipeDetailsPage />} />
              <Route path="/u/:userId/recipes/:recipeId/cook" element={<CookViewPage />} />
              {/* Friendly URL routes */}
              <Route path="/recipe/:slugWithId" element={<RecipeDetailsPage />} />
              <Route path="/recipe/:slugWithId/cook" element={<CookViewPage />} />
              <Route path="/plan/:slugWithId" element={<MealPlanPage />} />
              <Route path="/pantry" element={<PantryPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/billing" element={<BillingPage />} />
              {/* All admin routes share AdminLayout: the admin guard, the
                  section nav, and the impersonation banner. */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverviewPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="seed-queue" element={<SeedQueuePage />} />
                <Route path="catalog" element={<CatalogBrowserPage />} />
                <Route path="trending" element={<TrendingBrowserPage />} />
                <Route path="taste-axes" element={<TasteAxesPage />} />
                <Route path="taste-axes/:axisId" element={<TasteAxisDetailPage />} />
                <Route path="ingredients" element={<IngredientBrowserPage />} />
                <Route path="ingredients/:name" element={<IngredientDetailPage />} />
                <Route path="catalog-recipe/:recipeId" element={<CatalogRecipeDetailsPage />} />
                <Route path="imports" element={<AdminImportsPage />} />
                <Route path="releases" element={<AdminReleasesPage />} />
                <Route path="vouchers" element={<AdminVouchersPage />} />
                <Route path="emails" element={<AdminEmailsPage />} />
                <Route path="system" element={<AdminSystemPage />} />
              </Route>
              <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
              <Route path="/subscription/canceled" element={<SubscriptionCanceledPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ResponsiveContainer>
        </Box>
        
        <AppFooter />
      </Stack>
    );
  }

  // Fallback loading state (shouldn't normally reach here)
  return <LoadingScreen />;
}

function App() {
  return (
    <>
      {/* Default head tags (title/description/OG) live in public/index.html and
          are served on every full page load. Recipe/profile/plan routes have
          their head tags injected server-side (meals-you-love og.ts). The app
          deliberately renders no default meta here — React 19 appends rather
          than overrides metadata, so doing so produced duplicate/conflicting
          tags. Per-page tab titles are handled with the usePageTitle hook,
          which sets the page's title and restores the index.html default on
          unmount so titles never "stick" across client-side navigation. */}
      <ConditionalPostHogProvider>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={{
          ...theme,
          components: {
            Button: {
              styles: {
                root: {
                  borderRadius: '28px',
                },
              },
            },
            ActionIcon: {
              styles: {
                root: {
                  borderRadius: '28px',
                },
              },
            },
            // Round every input to match the pill buttons and lg-rounded
            // cards they sit between; without this they render Mantine's 4px
            // default. Defaults set on `Input` do NOT reach these components
            // (each field resolves its CSS vars from its own wrapper's props,
            // so only a bare inner <Input> would see them) — every
            // Input-based component needs its own entry, so when adding a new
            // input type (FileInput, PinInput, …) add it here too. Fields
            // that pass their own radius (e.g. CountStepper's `md`) still
            // win.
            ...Object.fromEntries(
              [
                'TextInput',
                'Textarea',
                'Select',
                'NativeSelect',
                'NumberInput',
                'PasswordInput',
                'MultiSelect',
                'Autocomplete',
                'TagsInput',
                // @mantine/dates fields
                'DatePickerInput',
                'TimeInput',
              ].map((component) => [component, { defaultProps: { radius: 'lg' } }])
            ),
            Modal: {
              defaultProps: {
                radius: 'lg',
              },
            },
            SegmentedControl: {
              defaultProps: {
                radius: 'md',
              },
            },
            Drawer: {
              styles: {
                content: {
                  borderTopLeftRadius: 'var(--mantine-radius-lg)',
                  borderBottomLeftRadius: 'var(--mantine-radius-lg)',
                },
              },
            },
            // Unified overlay surfaces: every popover, menu, and tooltip shares
            // the app's soft rounding (and a consistent shadow for the popped
            // surfaces) so info popovers, dropdown menus, and hover labels all
            // read as one system. Tooltips keep Mantine's dark surface — higher
            // contrast for tiny hover labels — but are rounded to match.
            Popover: {
              defaultProps: {
                radius: 'md',
                shadow: 'md',
              },
            },
            Menu: {
              defaultProps: {
                radius: 'md',
                shadow: 'md',
              },
            },
            Tooltip: {
              defaultProps: {
                radius: 'md',
              },
            },
            Notification: notificationTheme,
          },
        }}>
          <ModalsProvider modalProps={{ centered: true, radius: 'lg' }}>
            <OnboardingProvider>
            <CookingTimersProvider>
              <Notifications
                transitionDuration={200}
                limit={3}
                // Keep toasts inset from the viewport edges on phones
                // (the default 440px container renders flush-left at <440px)
                // while preserving Mantine's 440px cap on wider screens
                styles={{ root: { maxWidth: 'min(440px, calc(100vw - 24px))' } }}
              />
              <IngredientCatalogLoader />
              <AppUpdateNotifier />
              <BrowserRouter>
                <PostHogUserIdentifier>
                  <ScrollToTopWrapper>
                    <Routes>
                      {/* Public routes - accessible without authentication */}
                      <Route path="/about" element={<PageLayout><AboutPage /></PageLayout>} />
                      <Route path="/privacy" element={<PageLayout><PrivacyPolicyPage /></PageLayout>} />
                      <Route path="/terms" element={<PageLayout><TermsOfServicePage /></PageLayout>} />
                      <Route path="/cookies" element={<PageLayout><CookiePolicyPage /></PageLayout>} />
                      <Route path="/auth/action" element={<PageLayout><AuthActionPage /></PageLayout>} />
                      {/* Dedicated auth page: the route carries the referring
                          intent (marketing CTAs → /signup, header sign-in →
                          /signin). Standalone full-viewport layout — no app
                          header/footer — and it redirects signed-in visitors
                          back out itself. */}
                      <Route path="/signup" element={<AuthPage mode="signup" />} />
                      <Route path="/signin" element={<AuthPage mode="signin" />} />
                      <Route path="/personalized" element={<MarketingRoute><CampaignLandingPage /></MarketingRoute>} />
                      <Route path="/lifestyle" element={<MarketingRoute><LifestyleLandingPage /></MarketingRoute>} />
                      {/* All other routes handled by AppContent */}
                      <Route path="*" element={<AppContent />} />
                    </Routes>
                  </ScrollToTopWrapper>
                </PostHogUserIdentifier>
                <CookieConsentComponent />
                {/* Inside the router: tapping a timer navigates to its step. */}
                <CookingTimersTray />
              </BrowserRouter>
            </CookingTimersProvider>
            </OnboardingProvider>
          </ModalsProvider>
        </MantineProvider>
      </QueryClientProvider>
      </ConditionalPostHogProvider>
    </>
  );
}

export default App;
